import React, { createContext, useContext, useState, ReactNode, useEffect, createElement } from 'react';
import * as domain from '../utils/domain-rules';
import { availabilityApi } from '../api/availabilityApi';
import { useUnsavedChanges } from '../../../context/UnsavedChangesContext';
export type AgendaView = 'day' | 'week' | 'month';
export type EditorTab = 'availability' | 'date-adjustments';
export type AvailabilityEditMode = 'standard' | 'edit-availability';

export interface AvailabilityBlock {
  id: string;
  weekday: number;
  start: string;
  end: string;
}

// DateInterval is a time slot override for a specific date
export interface DateInterval {
  start: string;
  end: string;
}

export interface ReservationBlock {
  id: string;
  weekday: number;
  start: string;
  end: string;
}

// dateOverrides maps ISO date strings to their override intervals
// If a date has an entry here, it overrides the habitual schedule
export interface DirectAvailabilityUiState {
  currentDate: string;
  view: AgendaView;
  editorTab: EditorTab;
  editMode: AvailabilityEditMode;
  selectedAvailabilityId: string | null;
  draft: { weekday: number; start: string; end: string } | null;
  selectedApplyDays: Set<number>;
  // Date adjustments
  adjustmentDate: string;                         // ISO date currently shown in the date tab
  selectedDateIntervalIndex: number | null;        // which interval is selected
  dateDraft: { date: string; start: string; end: string; originalBlockId?: string } | null; // newly drawn draft on day/week when in date tab
  dateOverrides: Map<string, DateInterval[]>;      // overrides by ISO date
  dateEditMode: 'normal' | 'adjust';               // adjust = editing within override slot
  pauseMinutes: 0 | 5 | 10 | 15 | 20 | 30;
  dirty: boolean;
  availabilities: AvailabilityBlock[];
  reservations: ReservationBlock[];
  slotsByDate: Record<string, DateInterval[]>;
}

export interface DeleteModalState {
  open: boolean;
  type: 'habitual' | 'override';
  id?: string;
  date?: string;
  index?: number;
  info?: string;
}


interface DirectAvailabilityContextType {
  state: DirectAvailabilityUiState;
  actions: any;
  loading: boolean;
  error: string | null;
  hasChanges: boolean;
  isReminderActive: boolean;
  isSaving: boolean;
  lastSaved: boolean;
  showUnsavedModal: boolean;
  setShowUnsavedModal: (show: boolean) => void;
  isReplicateOpen: boolean;
  setIsReplicateOpen: (open: boolean) => void;
  deleteModalState: DeleteModalState;
  clearReminder: () => void;
  loadData: () => Promise<void>;
  saveData: () => Promise<void>;
  replicateData: (untilDate: string) => Promise<void>;
  clearError: () => void;
}

const DirectAvailabilityContext = createContext<DirectAvailabilityContextType | null>(null);

export const useDirectAvailability = () => {
  const context = useContext(DirectAvailabilityContext);
  if (!context) throw new Error('useDirectAvailability must be used within DirectAvailabilityProvider');
  return context;
};

export const DirectAvailabilityProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [isReplicateOpen, setIsReplicateOpen] = useState(false);
  const [deleteModalState, setDeleteModalState] = useState<DeleteModalState>({
    open: false,
    type: 'habitual'
  });
  const { setIsDirty, isReminderActive, clearReminder } = useUnsavedChanges();

  useEffect(() => {
    if (isReminderActive) setShowUnsavedModal(true);
  }, [isReminderActive]);

  const today = new Date().toISOString().split('T')[0];

  const [state, setState] = useState<DirectAvailabilityUiState>({
    currentDate: today,
    view: 'week',
    editorTab: 'availability',
    editMode: 'standard',
    selectedAvailabilityId: null,
    draft: null,
    selectedApplyDays: new Set<number>(),
    adjustmentDate: today,
    selectedDateIntervalIndex: null,
    dateDraft: null,
    dateOverrides: new Map(),
    dateEditMode: 'normal',
    pauseMinutes: 15,
    dirty: false,
    availabilities: [],
    reservations: [],
    slotsByDate: {}
  });

  const hasChanges = state.dirty || state.draft !== null || state.dateDraft !== null;

  useEffect(() => {
    setIsDirty(hasChanges);
  }, [hasChanges, setIsDirty]);

  useEffect(() => {
    if (hasChanges) {
      setLastSaved(false);
    }
  }, [hasChanges]);

  const loadWeekSlots = async (date: string) => {
    try {
      const d = new Date(date + 'T00:00:00');
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(d);
      monday.setDate(diff);
      const isoDate = monday.toISOString().split('T')[0];

      const data = await availabilityApi.fetchWeekSlots(isoDate);
      const newAvailabilities: AvailabilityBlock[] = [];
      Object.entries(data.slots_by_date).forEach(([iso, slots]) => {
        const dateObj = new Date(iso + 'T00:00:00');
        const weekday = dateObj.getDay() === 0 ? 7 : dateObj.getDay();
        slots.forEach(slot => {
          newAvailabilities.push({
            id: Math.random().toString(36).substring(2, 9),
            weekday,
            start: slot.start,
            end: slot.end
          });
        });
      });

      setState(s => ({
        ...s,
        slotsByDate: data.slots_by_date,
        availabilities: newAvailabilities,
        dirty: false,
        selectedAvailabilityId: null,
        draft: null,
        dateDraft: null
      }));
    } catch (e) {
      console.error('Error fetching week slots', e);
    }
  };

  useEffect(() => {
    loadWeekSlots(state.currentDate);
  }, [state.currentDate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await availabilityApi.fetchAvailabilityData();
      
      const overridesMap = new Map<string, DateInterval[]>();
      Object.entries(data.dateOverrides).forEach(([k, v]) => {
        overridesMap.set(k, v);
      });

      setState(s => ({
        ...s,
        reservations: data.reservations,
        dateOverrides: overridesMap,
        pauseMinutes: data.pauseMinutes,
        dirty: false
      }));
    } catch (e: any) {
      setError(e.message || 'Error loading availability data');
    } finally {
      setLoading(false);
    }
  };

  const saveData = async () => {
    try {
      setIsSaving(true);
      setError(null);
      
      const overridesObj: Record<string, DateInterval[]> = {};
      state.dateOverrides.forEach((v, k) => {
        overridesObj[k] = v;
      });

      const d = new Date(state.currentDate + 'T00:00:00');
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(d);
      monday.setDate(diff);
      const weekFrom = monday.toISOString().split('T')[0];

      await availabilityApi.saveAvailabilityData({
        week_from: weekFrom,
        availabilities: state.availabilities,
        dateOverrides: overridesObj,
        pauseMinutes: state.pauseMinutes
      });
      
      await loadWeekSlots(state.currentDate);
      
      // Clear draft on save — drafts are not persisted to the backend,
      // keeping them after save would immediately re-trigger isDirty.
      setState(s => ({ ...s, dirty: false, draft: null, dateDraft: null }));
      setLastSaved(true);
    } catch (e: any) {
      setError(e.message || 'Error saving availability data');
    } finally {
      setIsSaving(false);
    }
  };

  const replicateData = async (untilDate: string) => {
    try {
      setIsSaving(true);
      setError(null);
      const d = new Date(state.currentDate + 'T00:00:00');
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(d);
      monday.setDate(diff);
      const weekFrom = monday.toISOString().split('T')[0];

      await availabilityApi.replicateAvailability(untilDate, weekFrom);
      await loadWeekSlots(state.currentDate);
    } catch (e: any) {
      const backendMsg = e?.response?.data?.error;
      setError(backendMsg || e.message || 'Error al replicar disponibilidad');
      throw e; // re-throw so the modal can catch and show inline error
    } finally {
      setIsSaving(false);
    }
  };

  const actions = {
    setView: (view: AgendaView) => setState(s => ({ ...s, view })),
    setCurrentDate: (date: string) => setState(s => ({ ...s, currentDate: date })),
    setEditorTab: (editorTab: EditorTab) => setState(s => ({ ...s, editorTab })),
    setDraft: (draft: { weekday: number; start: string; end: string } | null) => setState(s => ({
      ...s, draft,
      selectedApplyDays: draft ? new Set([draft.weekday]) : s.selectedApplyDays,
      editorTab: 'availability'
    })),
    setApplyDays: (days: Set<number>) => setState(s => ({ ...s, selectedApplyDays: days })),

    // Weekly availability CRUD
    createAvailability: (block: Omit<AvailabilityBlock, 'id'>) => {
      setState(s => {
        const arr = [...s.availabilities];
        const res = domain.createAvailability(arr, block) as any;
        if (res.ok && res.item) {
          return { ...s, availabilities: arr, draft: null, selectedAvailabilityId: res.item.id, dirty: true };
        }
        return s;
      });
    },
    updateAvailability: (id: string, start: string, end: string) => {
      setState(s => {
        const arr = [...s.availabilities];
        const res = domain.updateAvailability(arr, id, { start, end }, s.reservations as any) as any;
        if (res.ok) {
          return { ...s, availabilities: arr, dirty: true };
        }
        return s;
      });
    },
    deleteAvailability: (id: string) => {
      setState(s => ({
        ...s,
        availabilities: s.availabilities.filter(a => a.id !== id),
        selectedAvailabilityId: s.selectedAvailabilityId === id ? null : s.selectedAvailabilityId,
        dirty: true
      }));
    },
    selectAvailability: (id: string | null) => {
      setState(s => {
        const block = id ? s.availabilities.find(a => a.id === id) : null;
        return {
          ...s,
          selectedAvailabilityId: id,
          draft: null,
          editMode: 'standard',
          selectedApplyDays: block ? new Set([block.weekday]) : new Set<number>()
        };
      });
    },
    setEditMode: (mode: AvailabilityEditMode) => setState(s => ({ ...s, editMode: mode })),
    editAvailabilityCut: (id: string, start: string, end: string) => {
      setState(s => {
        const arr = [...s.availabilities];
        const res = domain.editAvailability(arr, id, start, end, s.reservations as any) as any;
        if (res.ok) {
          return { ...s, availabilities: arr, editMode: 'standard', selectedAvailabilityId: null, dirty: true };
        }
        return s;
      });
    },
    applyToDays: (sourceDay: number, targetDays: number[], start: string, end: string, originalRange?: {start: string, end: string}, isCut?: boolean) => {
      setState(s => {
        const arr = [...s.availabilities];
        if (isCut) {
          domain.applyAvailabilityEditToDays(arr, sourceDay, targetDays, start, end, s.reservations as any);
        } else if (originalRange) {
          domain.updateCounterparts(arr, sourceDay, targetDays, originalRange, { start, end }, s.reservations as any);
        } else {
          domain.applyRangeToDays(arr, sourceDay, targetDays, { start, end });
        }
        return { ...s, availabilities: arr, dirty: true };
      });
    },

    // Date adjustments actions
    setAdjustmentDate: (date: string) => setState(s => ({
      ...s,
      adjustmentDate: date,
      selectedDateIntervalIndex: null,
      dateDraft: null,
      dateEditMode: 'normal'
    })),
    selectDateInterval: (index: number | null) => setState(s => ({
      ...s,
      selectedDateIntervalIndex: index,
      dateDraft: null,
      dateEditMode: 'normal'
    })),
    setDateDraft: (draft: { date: string; start: string; end: string; originalBlockId?: string } | null) => setState(s => ({
      ...s,
      dateDraft: draft,
      selectedDateIntervalIndex: null,
      dateEditMode: 'normal'
    })),
    setDateEditMode: (mode: 'normal' | 'adjust') => setState(s => ({ ...s, dateEditMode: mode })),

    // Create a date override interval (from dateDraft)
    createDateInterval: (date: string, start: string, end: string, originalBlockId?: string) => {
      setState(s => {
        const overrides = new Map(s.dateOverrides);
        let list: {start: string, end: string, id?: string}[] = [];
        
        if (overrides.has(date)) {
          list = [...overrides.get(date)!];
        } else {
          // Initialize with habitual blocks for this day of week
          const dateObj = new Date(date + 'T00:00:00');
          const weekday = dateObj.getDay() === 0 ? 7 : dateObj.getDay();
          list = s.availabilities
            .filter(a => a.weekday === weekday)
            .map(a => ({ start: a.start, end: a.end, id: a.id }));
        }

        if (originalBlockId) {
          const idx = list.findIndex(a => a.id === originalBlockId);
          if (idx >= 0) {
            list[idx] = { start, end };
          } else {
            list.push({ start, end });
          }
        } else {
          list.push({ start, end });
        }

        // Clean up ids and sort
        const finalList = list.map(a => ({ start: a.start, end: a.end }));
        finalList.sort((a, b) => a.start.localeCompare(b.start));
        overrides.set(date, finalList);

        return { ...s, dateOverrides: overrides, dateDraft: null, selectedDateIntervalIndex: finalList.findIndex(a => a.start === start && a.end === end), dirty: true };
      });
    },

    // Update an existing override interval
    updateDateInterval: (date: string, index: number, start: string, end: string) => {
      setState(s => {
        const overrides = new Map(s.dateOverrides);
        let list: { start: string; end: string }[] = [];
        if (overrides.has(date)) {
          list = [...overrides.get(date)!];
        } else {
          const dateObj = new Date(date + 'T00:00:00');
          const weekday = dateObj.getDay() === 0 ? 7 : dateObj.getDay();
          list = s.availabilities
            .filter(a => a.weekday === weekday)
            .map(a => ({ start: a.start, end: a.end }));
        }
        if (list[index]) {
          list[index] = { start, end };
          list.sort((a, b) => a.start.localeCompare(b.start));
          overrides.set(date, list);
        }
        return { ...s, dateOverrides: overrides, dirty: true };
      });
    },

    // Delete a specific override interval
    deleteDateInterval: (date: string, index: number) => {
      setState(s => {
        const overrides = new Map(s.dateOverrides);
        let list: { start: string; end: string }[] = [];
        if (overrides.has(date)) {
          list = [...overrides.get(date)!];
        } else {
          const dateObj = new Date(date + 'T00:00:00');
          const weekday = dateObj.getDay() === 0 ? 7 : dateObj.getDay();
          list = s.availabilities
            .filter(a => a.weekday === weekday)
            .map(a => ({ start: a.start, end: a.end }));
        }
        if (index >= 0 && index < list.length) {
          list.splice(index, 1);
        }
        overrides.set(date, list);
        return { ...s, dateOverrides: overrides, selectedDateIntervalIndex: null, dateDraft: null, dirty: true };
      });
    },


    // Restore habitual schedule for a date (delete override)
    restoreDateHabitual: (date: string) => {
      setState(s => {
        const overrides = new Map(s.dateOverrides);
        overrides.delete(date);
        return { ...s, dateOverrides: overrides, selectedDateIntervalIndex: null, dateDraft: null, dateEditMode: 'normal', dirty: true };
      });
    },

    // Global settings
    setPauseMinutes: (minutes: 0 | 5 | 10 | 15 | 20 | 30) => {
      setState(s => ({ ...s, pauseMinutes: minutes, dirty: true }));
    },

    // Delete confirmation modal
    openDeleteHabitualModal: (id: string, info?: string) => {
      setDeleteModalState({ open: true, type: 'habitual', id, info });
    },
    openDeleteOverrideModal: (date: string, index: number, info?: string) => {
      setDeleteModalState({ open: true, type: 'override', date, index, info });
    },
    closeDeleteModal: () => {
      setDeleteModalState(prev => ({ ...prev, open: false }));
    },
    confirmDeleteModal: () => {
      setDeleteModalState(prev => {
        if (prev.type === 'habitual' && prev.id) {
          actions.deleteAvailability(prev.id);
        } else if (prev.type === 'override' && prev.date !== undefined && prev.index !== undefined) {
          actions.deleteDateInterval(prev.date, prev.index);
        }
        return { ...prev, open: false };
      });
    },
    openReplicateModal: () => setIsReplicateOpen(true),
    closeReplicateModal: () => setIsReplicateOpen(false),
  };

  return createElement(DirectAvailabilityContext.Provider, {
    value: {
      state,
      actions,
      loading,
      isSaving,
      lastSaved,
      error,
      hasChanges,
      isReminderActive,
      showUnsavedModal,
      setShowUnsavedModal,
      isReplicateOpen,
      setIsReplicateOpen,
      deleteModalState,
      clearReminder,
      loadData,
      saveData,
      replicateData,
      clearError: () => setError(null)
    }
  }, children);
};


